import { useCallback, useEffect, useState } from "react";
import {
  Button,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Icon,
  IconButton,
  MenuButton,
  Spinner,
  Tag,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import type { CkMenuItem } from "@NP-Webber/crm-kit/dist/next/index.js";
import { nextApi, type AutomationDto, type AutomationRunDto, type AutomationTemplateDto } from "../../api/nextApi";
import { notifyAutomationsChanged, notifyDataChanged } from "../events";
import { ACTION_ICON, describeRun, fmtWhen, RUN_STATUS_META, STATUS_META } from "./automationsStore";

const errMsg = (e: unknown, fallback: string) => (e as { message?: string })?.message || fallback;

/* ── automation card ── */
export function AutomationCard({
  a,
  canManage,
  busy,
  screenLabel,
  onToggle,
  onRun,
  onEdit,
  onHistory,
  onDelete,
}: {
  a: AutomationDto;
  canManage: boolean;
  busy?: boolean;
  screenLabel?: string;
  onToggle: (a: AutomationDto, on: boolean) => void;
  onRun: (a: AutomationDto, dryRun?: boolean) => void;
  onEdit: (a: AutomationDto) => void;
  onHistory: (a: AutomationDto) => void;
  onDelete: (a: AutomationDto) => void;
}) {
  const st = STATUS_META[a.status] ?? STATUS_META.paused;
  const menu: CkMenuItem[] = [
    { key: "history", label: "היסטוריית הרצות", icon: "history", onClick: () => onHistory(a) },
    ...(canManage
      ? ([
          a.kind === "schedule" ? { key: "run", label: "להריץ עכשיו", icon: "play", onClick: () => onRun(a) } : null,
          a.kind === "schedule" ? { key: "dry", label: "בדיקה בלי לבצע", icon: "eye", onClick: () => onRun(a, true) } : null,
          { key: "edit", label: "עריכה", icon: "edit", onClick: () => onEdit(a) },
          { key: "delete", label: "מחיקה", icon: "trash", danger: true, onClick: () => onDelete(a) },
        ].filter(Boolean) as CkMenuItem[])
      : []),
  ];
  return (
    <article className={`atm-card atm-card--${a.status}`}>
      <div className="atm-card__icon">
        <Icon name={ACTION_ICON[a.actions[0]?.type ?? ""] || "sparkle"} size={18} />
      </div>
      <div className="atm-card__main">
        <div className="atm-card__top">
          <b className="atm-card__name">{a.name}</b>
          <Tag tone={st.tone}>{st.label}</Tag>
          {screenLabel ? <Tag tone="neutral">{screenLabel}</Tag> : null}
        </div>
        <p className="atm-card__when">
          <Icon name={a.kind === "event" ? "bell" : "clock"} size={13} /> {a.summary}
        </p>
        {a.description ? <p className="atm-card__what">{a.description}</p> : null}
        <div className="atm-card__meta">
          <span>{a.kind === "event" ? "לפי אירוע" : "מתוזמנת"}</span>
          {a.status !== "paused" && a.nextRunAt ? <span>הרצה הבאה: {fmtWhen(a.nextRunAt)}</span> : null}
          {a.lastRunAt ? <span>אחרונה: {fmtWhen(a.lastRunAt)}</span> : <span>עוד לא רצה</span>}
          {a.runCount ? <span>{a.runCount.toLocaleString("he-IL")} הרצות</span> : null}
        </div>
        {a.lastError ? (
          <p className="atm-card__error">
            <Icon name="alert" size={13} /> {a.lastError}
          </p>
        ) : null}
      </div>
      <div className="atm-card__actions">
        {canManage ? (
          <label className="atm-switch" title={a.status === "paused" ? "הפעלה" : "השהיה"}>
            <input
              type="checkbox"
              role="switch"
              className="ck-switch"
              checked={a.status !== "paused"}
              disabled={busy}
              onChange={(e) => onToggle(a, e.target.checked)}
              aria-label={a.status === "paused" ? `הפעלת ${a.name}` : `השהיית ${a.name}`}
            />
          </label>
        ) : null}
        <MenuButton items={menu} width={220} renderButton={(p) => <IconButton icon="more" label="פעולות" size="sm" {...p} />} />
      </div>
    </article>
  );
}

/* ── template card ── */
export function TemplateCard({ t, canManage, busy, onAdd, onCustomize }: { t: AutomationTemplateDto; canManage: boolean; busy?: boolean; onAdd: () => void; onCustomize: () => void }) {
  return (
    <div className="atm-tpl">
      <div className="atm-card__icon">
        <Icon name={t.icon || "sparkle"} size={18} />
      </div>
      <div className="atm-tpl__main">
        <b>{t.name}</b>
        <p>{t.description}</p>
      </div>
      {canManage ? (
        <div className="atm-row">
          <Button size="sm" variant="ghost" onClick={onCustomize}>
            התאמה
          </Button>
          <Button size="sm" variant="primary" icon="plus" disabled={busy} onClick={onAdd}>
            הפעלה
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/* ── run history ── */
export function RunsList({ automationId, refreshKey = 0 }: { automationId: string; refreshKey?: number }) {
  const [runs, setRuns] = useState<AutomationRunDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setRuns(null);
    nextApi.automations
      .runs(automationId)
      .then((r) => alive && setRuns(r))
      .catch((e: unknown) => alive && setError(errMsg(e, "שגיאה בטעינה")));
    return () => {
      alive = false;
    };
  }, [automationId, refreshKey]);
  if (error) return <p className="atm-note atm-note--danger">{error}</p>;
  if (!runs)
    return (
      <div className="atm-busy">
        <Spinner size={22} />
      </div>
    );
  if (!runs.length)
    return (
      <EmptyState compact icon="history" title="עוד אין הרצות">
        כשהאוטומציה תרוץ — יופיע כאן מה נשלח ולמי.
      </EmptyState>
    );
  return (
    <ul className="atm-runs">
      {runs.map((r) => {
        const st = RUN_STATUS_META[r.status] ?? RUN_STATUS_META.success;
        const errors = r.summary.errors ?? [];
        return (
          <li key={r.id} className="atm-run">
            <div className="atm-run__top">
              <Tag tone={st.tone}>{st.label}</Tag>
              <span className="atm-run__when">{fmtWhen(r.startedAt)}</span>
              <span className="atm-run__src">{r.trigger === "manual" ? "ידנית" : r.trigger === "event" ? "אירוע" : "מתוזמן"}</span>
            </div>
            <p className="atm-run__summary">{r.errorMessage && r.status === "failed" ? `שגיאה: ${r.errorMessage}` : describeRun(r.summary)}</p>
            {errors.length ? (
              <details className="atm-run__details">
                <summary>פרטים</summary>
                <ul>
                  {errors.map((e, i) => (
                    <li key={i} className="is-failed">
                      {e}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export function RunsDialog({ a, onClose }: { a: AutomationDto | null; onClose: () => void }) {
  return (
    <Dialog open={Boolean(a)} onClose={onClose} title={a ? `היסטוריית הרצות · ${a.name}` : ""} icon="history" size="md">
      {a ? <RunsList automationId={a.id} /> : null}
    </Dialog>
  );
}

/** Shared actions on automation cards (panel + all-automations page). */
export function useAutomationActions(reload: () => void) {
  const toast = useCkToast();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AutomationDto | null>(null);
  const [history, setHistory] = useState<AutomationDto | null>(null);

  const toggle = useCallback(
    async (a: AutomationDto, on: boolean) => {
      setBusyId(a.id);
      try {
        await nextApi.automations.setStatus(a.id, on ? "active" : "paused");
        toast.success(on ? `„${a.name}” הופעלה` : `„${a.name}” הושהתה`);
        notifyAutomationsChanged();
        reload();
      } catch (e) {
        toast.error(errMsg(e, "העדכון נכשל"));
      } finally {
        setBusyId(null);
      }
    },
    [reload, toast],
  );

  const run = useCallback(
    async (a: AutomationDto, dryRun = false) => {
      setBusyId(a.id);
      try {
        const res = await nextApi.automations.run(a.id, dryRun);
        const text = describeRun(res.summary);
        if (res.status === "failed") toast.error(res.errorMessage || "ההרצה נכשלה");
        else toast.success(text);
        if (!dryRun) {
          notifyAutomationsChanged();
          if (res.summary.tasksCreated) notifyDataChanged("task");
        }
        reload();
      } catch (e) {
        toast.error(errMsg(e, "ההרצה נכשלה"));
      } finally {
        setBusyId(null);
      }
    },
    [reload, toast],
  );

  const remove = useCallback(async () => {
    const a = confirmDelete;
    if (!a) return;
    setBusyId(a.id);
    try {
      await nextApi.automations.remove(a.id);
      toast.success(`„${a.name}” נמחקה`);
      notifyAutomationsChanged();
      setConfirmDelete(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e, "המחיקה נכשלה"));
    } finally {
      setBusyId(null);
    }
  }, [confirmDelete, reload, toast]);

  const dialogs = (
    <>
      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => void remove()}
        busy={Boolean(busyId)}
        title="מחיקת אוטומציה"
        confirmLabel="מחיקה"
        message={confirmDelete ? `„${confirmDelete.name}” תיעצר ותימחק. מה שכבר בוצע (מיילים, משימות) נשאר.` : ""}
      />
      <RunsDialog a={history} onClose={() => setHistory(null)} />
    </>
  );

  return { busyId, toggle, run, askDelete: setConfirmDelete, showHistory: setHistory, dialogs };
}
